import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { useGetUniqueColor } from "./useGetUniqueColor";

import type { Section } from "./useWidgetTaskSingleSections";

import { taskExecutionListCheckTaskExecutions$key } from "./__generated__/taskExecutionListCheckTaskExecutions.graphql";
import { taskExecutionListCombineCheckTaskExecutions$key } from "./__generated__/taskExecutionListCombineCheckTaskExecutions.graphql";

export function useCombineCheckTaskExecutions(
  taskExecutionKeys: taskExecutionListCombineCheckTaskExecutions$key
) {
  const te = useFragment(
    graphql`
      fragment taskExecutionListCombineCheckTaskExecutions on TaskExecutionCheck
      @relay(plural: true) {
        checkValue
      }
    `,
    taskExecutionKeys
  );

  return useMemo(() => {
    const totalCompleted = te.filter((t) => t.checkValue !== null).length;
    const totalPositive = te.filter((t) => t.checkValue === "POSITIVE").length;

    return {
      percentage:
        totalCompleted === 0
          ? null
          : Math.floor((totalPositive / te.length) * 100),
      numberOfRest: te.length,
    };
  }, [te]);
}

export function useCheckTaskExecutionList(
  taskExecutionKeys: taskExecutionListCheckTaskExecutions$key,
  sections: Section[],
  maxCheckItems = 6
) {
  const getColor = useGetUniqueColor();
  const taskExecutions = useFragment(
    graphql`
      fragment taskExecutionListCheckTaskExecutions on TaskExecutionCheck
      @relay(plural: true) {
        id
        sectionId
        checkValue
      }
    `,
    taskExecutionKeys
  );

  return useMemo(() => {
    const tes = taskExecutions
      .map((te) => [te, sections.find((s) => s.id === te.sectionId)] as const)
      .filter(([te, s]) => !!s);

    if (tes.length === 1) {
      return tes.map(([te, section]) => {
        if (!section) {
          throw new Error();
        }
        return {
          checkValue: te.checkValue,
          id: te.id,
          color: getColor(section.assignedTeam?.color),
          sectionTitle: section.title,
        };
      });
    }

    const sorted = (tes ?? [])
      .slice()
      .sort(([_a, a], [_b, b]) => a!.ordinal - b!.ordinal)
      .map(([te, section]) => {
        if (!section) {
          throw new Error();
        }

        return {
          type: "item" as const,
          checkValue: te.checkValue,
          color: getColor(section.assignedTeam?.color),
          id: te.id,
          sectionTitle: section.title,
        };
      });

    const topItems =
      sorted.length > maxCheckItems
        ? sorted.slice(0, maxCheckItems - 1)
        : sorted;

    return topItems;
  }, [getColor, maxCheckItems, sections, taskExecutions]);
}
